import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PREREQUISITES, evaluatePrerequisitesPartial } from './prerequisites';
import { authFetch } from '../../auth';
import './PrerequisiteGate.css';

// Module-level cache for async checks so multiple gates on a session don't refetch.
// Key: requirement key. Value: boolean.
const asyncCache = {};
const inflight = {};

function runAsyncCheck(key) {
  if (key in asyncCache) return Promise.resolve(asyncCache[key]);
  if (inflight[key]) return inflight[key];
  const def = PREREQUISITES[key];
  if (!def || !def.asyncCheck) return Promise.resolve(true);
  inflight[key] = def.asyncCheck(authFetch).then((result) => {
    asyncCache[key] = !!result;
    delete inflight[key];
    return asyncCache[key];
  }).catch(() => {
    asyncCache[key] = false;
    delete inflight[key];
    return false;
  });
  return inflight[key];
}

export function invalidatePrerequisiteCache(key) {
  if (key) delete asyncCache[key];
  else Object.keys(asyncCache).forEach(k => delete asyncCache[k]);
}

const PrerequisiteGate = ({ requires = [], user, featureLabel, children }) => {
  const [asyncResults, setAsyncResults] = useState(() => {
    const seed = {};
    for (const k of requires) if (k in asyncCache) seed[k] = asyncCache[k];
    return seed;
  });
  const [resolvingAsync, setResolvingAsync] = useState(() =>
    requires.some(k => PREREQUISITES[k]?.asyncCheck && !(k in asyncCache))
  );

  useEffect(() => {
    const needed = requires.filter(k => PREREQUISITES[k]?.asyncCheck && !(k in asyncCache));
    if (needed.length === 0) {
      setResolvingAsync(false);
      return;
    }
    let cancelled = false;
    Promise.all(needed.map(k => runAsyncCheck(k).then(v => [k, v]))).then(pairs => {
      if (cancelled) return;
      const next = { ...asyncResults };
      for (const [k, v] of pairs) next[k] = v;
      setAsyncResults(next);
      setResolvingAsync(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requires.join('|')]);

  if (resolvingAsync) {
    return (
      <div className="prereq-gate" data-testid="prereq-gate-loading">
        <div className="prereq-gate-card prereq-gate-card-loading">
          <div className="prereq-gate-spinner" />
        </div>
      </div>
    );
  }

  const { met, unmet } = evaluatePrerequisitesPartial(requires, user, asyncResults);
  if (met) return children;

  const first = unmet[0];

  return (
    <div className="prereq-gate" data-testid="prereq-gate">
      <div className="prereq-gate-card">
        <div className="prereq-gate-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        <h2 className="prereq-gate-title">
          {featureLabel ? `${featureLabel} isn’t ready yet` : 'A step is needed first'}
        </h2>

        <p className="prereq-gate-sub">
          {unmet.length === 1
            ? 'One quick step and you’re in:'
            : `${unmet.length} steps to unlock this:`}
        </p>

        <ol className="prereq-gate-steps">
          {unmet.map((step, i) => (
            <li key={step.key} className={i === 0 ? 'is-next' : ''}>
              <span className="prereq-step-num">{i + 1}</span>
              <div className="prereq-step-body">
                <div className="prereq-step-title">{step.title}</div>
                <div className="prereq-step-desc">{step.description}</div>
                {step.note && <div className="prereq-step-note">{step.note}</div>}
              </div>
            </li>
          ))}
        </ol>

        <Link
          to={first.fixPath}
          className="prereq-gate-cta"
          data-testid="prereq-gate-cta"
        >
          {first.fixLabel}
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M13 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </div>
  );
};

export default PrerequisiteGate;
